// ---------------------------------------------------------------------------
// How a LinkedIn sending account's seat is described to a person
//
// One place, because the same fact is shown while connecting an account and
// again while picking accounts for a campaign, and a description that drifts
// between the two is worse than no description at all.
//
// WHY THE SEAT IS ON SCREEN
//
// It decides whether an invitation may carry a personal note. LinkedIn rations
// note-carrying invitations per account per month on a free seat; a paid seat
// reports no monthly cap. From 4 to 15 August 2026 a live campaign sent zero
// invitations for eleven days because its four free seats had spent that
// ration and nothing anywhere said so.
//
// THREE STATES, NOT TWO
//
// `null` is not `[]`. An empty list is an observation that the seat is free; a
// missing value is the absence of an observation, and the two must never render
// the same. Guessing "free" for an unread seat is how a Recruiter account gets
// treated as rationed, and guessing "paid" is how a free one silently burns
// invitations on a note that will be refused.
// ---------------------------------------------------------------------------

/** How LinkedIn's own product names the features the API reports as slugs. */
const FEATURE_LABELS: Record<string, string> = {
  premium: 'Premium',
  sales_navigator: 'Sales Navigator',
  recruiter: 'Recruiter',
};

export type SeatKind = 'paid' | 'free' | 'unknown';

export interface SeatDescription {
  kind: SeatKind;
  /** The badge text. Short — it sits beside an account name. */
  label: string;
  /** The full sentence, for a tooltip. Says what it means, never a count. */
  title: string;
}

/**
 * Describe a seat from `premiumFeatures` as the provider reported it.
 *
 * `undefined` and `null` both mean "not observed" — the caller may not have
 * fetched it yet, or the payload may not have carried it. Neither is evidence.
 *
 * No number appears in any of these strings. LinkedIn publishes five notes a
 * month for a free seat; our own measurement says at least two. The two do not
 * agree, we have not measured the boundary, and a figure on screen would read
 * as a promise the product cannot keep.
 */
export function describeSeat(premiumFeatures?: string[] | null): SeatDescription {
  if (premiumFeatures === undefined || premiumFeatures === null) {
    return {
      kind: 'unknown',
      label: 'Seat unknown',
      title:
        'LinkedIn has not told us what kind of seat this account is yet. Until it does, invitations from it go ' +
        'out without a personal note.',
    };
  }

  if (premiumFeatures.length === 0) {
    return {
      kind: 'free',
      label: 'Free account',
      // Says only what the code does. An earlier version promised the note was
      // re-sent as the first message after acceptance — that is a line from
      // ADR-0006 describing intent, and nothing implements it.
      title:
        'A free LinkedIn account may only attach a personal note to a limited number of invitations each month. ' +
        'Once that is used up the invitation still goes out, without the note, so the lead is still reached.',
    };
  }

  const named = premiumFeatures.map(f => FEATURE_LABELS[f] ?? f).join(', ');
  return {
    kind: 'paid',
    label: named,
    title: `A paid LinkedIn seat (${named}). Invitations from this account can carry a personal note without a monthly limit.`,
  };
}

/** The shape the note notice needs off an account, and nothing more. */
export interface SeatBearingAccount {
  name?: string;
  id?: string;
  premiumFeatures?: string[] | null;
  /**
   * The calendar month this account's note allowance was last spent, `YYYY-MM`.
   * Null means unobserved, which is not the same as "still available".
   */
  noteRationSpentForMonth?: string | null;
}

export interface NoteNotice {
  /** Whether there is anything worth telling the person at all. */
  show: boolean;
  /**
   * WHY the note may not be sent — the question the customer asked for, and
   * three different answers rather than one general rule.
   *
   * `switched_off`  notes are off for every account; nothing carries one
   * `spent`         free seat, allowance already used up THIS month
   * `rationed`      free seat, allowance may still be there but will run out
   */
  kind: 'switched_off' | 'spent' | 'rationed';
  /** Accounts the message names, in the order they were given. */
  accountNames: string[];
}

/** A free or unobserved seat — anything that is not confirmed paid. */
const isNotPaid = (a: SeatBearingAccount): boolean => describeSeat(a.premiumFeatures).kind !== 'paid';

const displayName = (a: SeatBearingAccount): string => (a.name ?? '').trim() || (a.id ?? '').trim();

/**
 * What to tell someone writing a connection note — and only when it applies.
 *
 * THREE conditions, all required, because a notice that shows when it does not
 * apply is a notice people learn to dismiss:
 *
 *   - the step is a LinkedIn connect step
 *   - a note is actually written in it
 *   - at least one account that will send it is not a paid seat
 *
 * The first two belong to the caller, which is the only place that knows the
 * step. This owns the third and, more importantly, WHICH REASON applies.
 *
 * An UNOBSERVED seat counts as not-paid on purpose: we do not know it can carry
 * a note, and the failure mode of staying silent is someone believing a note
 * went out when it did not. `describeSeat` keeps the three seat states apart;
 * this collapses two of them, because the reader's question — "will my note be
 * sent?" — has the same answer for both.
 *
 * `sendingAccountIds` SCOPES all of it to one campaign, and is not optional.
 * The caller hands in every LinkedIn account the workspace has — that is what
 * `/api/accounts/linkedin` returns — and until 17 August every one of them was
 * named. Seven colleagues appeared under a sentence about a campaign that sends
 * from four. The statement was true of each account and false of the campaign,
 * which is the worst kind of wrong: nothing on screen looks like a bug.
 *
 * An EMPTY scope is a legitimate state, not an error — the wizard reaches the
 * sequence step before the Sales Rep step is answered — and it renders nothing.
 * There is deliberately no "unscoped" spelling that falls back to every
 * account: that fallback is the defect, and a required argument is what stops a
 * later caller from re-acquiring it by accident.
 */
export function noteNoticeFor(
  accounts: readonly SeatBearingAccount[],
  opts: {
    notesEnabled: boolean;
    hasNoteOnConnectStep: boolean;
    thisMonth: string;
    /** The accounts THIS campaign sends from. Empty means none chosen yet. */
    sendingAccountIds: readonly string[];
  },
): NoteNotice {
  // Nothing is being written, so there is nothing to warn about losing.
  if (!opts.hasNoteOnConnectStep) return { show: false, kind: 'rationed', accountNames: [] };

  // An intersection, not a lookup by id: an id with no account behind it — a
  // disconnected seat, a listing that came back short — must name nobody rather
  // than contribute an empty row to the sentence.
  const scope = new Set(opts.sendingAccountIds);
  const sending = accounts.filter(a => a.id !== undefined && scope.has(a.id));

  const affected = sending.filter(isNotPaid);
  if (affected.length === 0) return { show: false, kind: 'rationed', accountNames: [] };

  // Switched off outranks the seat: it applies to every account, so naming the
  // free ones would point at the wrong cause.
  if (!opts.notesEnabled) return { show: true, kind: 'switched_off', accountNames: [] };

  // Spent is the strongest thing we can say, and the only one that names a
  // consequence the reader can see today rather than a rule they might hit.
  // Observed per account: a null month is unobserved, never "still available".
  const spent = affected.filter(a => a.noteRationSpentForMonth === opts.thisMonth);
  if (spent.length > 0) return { show: true, kind: 'spent', accountNames: spent.map(displayName) };

  return { show: true, kind: 'rationed', accountNames: affected.map(displayName).filter(n => n.length > 0) };
}

/** DaisyUI badge classes per seat kind. Neutral for free — it is not a warning. */
export function seatBadgeClass(kind: SeatKind): string {
  if (kind === 'paid') return 'badge badge-success badge-sm';
  if (kind === 'free') return 'badge badge-ghost badge-sm';
  return 'badge badge-outline badge-sm';
}
